import { createComponent, EnvironmentInjector, EnvironmentProviders, inject, makeEnvironmentProviders, provideEnvironmentInitializer } from "@angular/core";
import { HighchartsChartComponent, provideHighcharts } from "highcharts-angular";
import { TranslateService } from "@ngx-translate/core";

import { AuthenticationService } from "./services/authentication/authentication.service";

// offline-exporting declares @requires highcharts/modules/exporting but has no import edge to
// it, so Promise.allSettled would leave the order to chance. Chain it explicitly.
export const highchartsModules = () => {
  const exporting = import("highcharts/esm/modules/exporting");
  return [
    import("highcharts/esm/highcharts-more"),
    exporting,
    exporting.then(() => import("highcharts/esm/modules/offline-exporting")),
    import("highcharts/esm/modules/boost"),
    import("highcharts/esm/modules/boost-canvas"),
    import("highcharts/esm/modules/stock"),
    import("highcharts/esm/modules/heatmap"),
    import("highcharts/esm/modules/xrange"),
    import("highcharts/esm/modules/no-data-to-display"),
  ];
};

// No Highcharts export server in this environment — force offline export for all types.
export const highchartsOptions = { exporting: { fallbackToExportServer: false } };

/**
 * Starts the Highcharts loader at bootstrap instead of leaving it to the first chart on screen.
 *
 * highcharts-angular creates a chart from an effect that reads the loader's signal. The loader is
 * asynchronous — core plus nine add-on modules — and is only kicked off when the first chart
 * component is constructed, so that chart's effect finds the signal still empty. It is never asked
 * again: neither a later change-detection pass on the chart's own view nor any interaction re-runs
 * it, and the component sits there as an empty white box for the rest of the session while every
 * chart created after the modules land draws normally. That is the "sometimes the chart is a white
 * box" shape — it is the *first* chart of a session that loses the race, and on the fleet overview
 * the trip chart is usually it.
 *
 * Constructing one throwaway chart component off-DOM is all it takes: its constructor asks the
 * root-scoped loader service to start, and the signal it sets outlives the component we destroy.
 */
function warmHighchartsLoader(): void {
  const environmentInjector = inject(EnvironmentInjector);

  try {
    const componentRef = createComponent(HighchartsChartComponent, { environmentInjector, hostElement: document.createElement("div") });
    componentRef.setInput("options", { series: [] });
    componentRef.destroy();
  } catch {
    // Warming is an optimisation, never a reason to fail bootstrap.
  }
}

// Registers the chart language and RTL mirroring at bootstrap. FhChartService stays lazy, so it still reads the
// logged-in user's timezone and distance unit.
function initChartLanguage(): void {
  const environmentInjector = inject(EnvironmentInjector);
  void import("./services/charts/chartLocalization")
    .then((m) => {
      const authenticationService = environmentInjector.get(AuthenticationService);
      m.registerChartLocalization(environmentInjector.get(TranslateService), () => authenticationService.getCultureLang());
    })
    .catch((error) => console.warn("Chart language/RTL setup failed", error));
}

export function provideAppHighcharts(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideHighcharts({
      timeout: 0,
      options: highchartsOptions,
      modules: highchartsModules,
    }),
    provideEnvironmentInitializer(warmHighchartsLoader),
    provideEnvironmentInitializer(initChartLanguage),
  ]);
}
